import { useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { ROLE_LABEL } from "@/lib/roles";
import {
  LayoutDashboard, Target, Trophy, GraduationCap, Users2,
  LogOut, Menu, X, Rocket, ChevronRight, Network, HeartHandshake, UserCircle,
  CreditCard, Palette, Swords,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MEMBER = ["admin", "agency_manager", "consultant", "staff"];

const NAV = [
  { to: "/app", label: "Dashboard", icon: LayoutDashboard, end: true, roles: MEMBER },
  { to: "/app/rede", label: "Rede", icon: Network, roles: ["master", "admin"] },
  { to: "/app/coaching", label: "Coaching", icon: HeartHandshake, roles: ["coach"] },
  { to: "/app/liga", label: "Liga", icon: Swords, roles: ["admin", "agency_manager", "consultant", "staff"] },
  { to: "/app/objetivos", label: "Objetivos", icon: Target, roles: ["admin", "agency_manager", "consultant", "staff"] },
  { to: "/app/ranking", label: "Ranking", icon: Trophy, roles: ["admin", "agency_manager", "consultant", "staff", "master"] },
  { to: "/app/academia", label: "Academia", icon: GraduationCap, roles: ["admin", "agency_manager", "consultant", "staff", "coach"] },
  { to: "/app/crm", label: "CRM", icon: Users2, roles: ["admin", "agency_manager"] },
  { to: "/app/faturacao", label: "Faturação", icon: CreditCard, roles: ["admin", "agency_manager"] },
  { to: "/app/marca", label: "Marca", icon: Palette, roles: ["agency_manager"] },
  { to: "/app/perfil", label: "O meu perfil", icon: UserCircle },
  { to: "/app/onboarding", label: "Onboarding", icon: Rocket, roles: ["consultant", "staff", "agency_manager"] },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const items = NAV.filter((n) => !n.roles || n.roles.includes(user?.role));
  const stats = user?.stats || {};
  const hasPoints = ["consultant", "staff", "agency_manager"].includes(user?.role);
  const branding = user?.agency?.branding || {};
  const accent = branding.accent_color || null;
  const agencyLogo = branding.logo_url ? `${BACKEND_URL}${branding.logo_url}` : null;

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const SidebarInner = () => (
    <div className="flex flex-col h-full">
      <div className="px-5 h-16 flex items-center border-b border-brand-border">
        {agencyLogo ? (
          <img src={agencyLogo} alt="Logótipo da agência" className="h-9 w-auto max-w-[170px] object-contain" data-testid="agency-logo" />
        ) : (
          <Logo className="h-8" withText />
        )}
      </div>

      <div className="px-4 py-5 border-b border-brand-border">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-sm bg-gradient-to-br from-brand-blue to-brand-orange flex items-center justify-center font-heading font-bold text-white text-lg shrink-0">
            {user?.name?.[0]?.toUpperCase() || "?"}
          </div>
          <div className="min-w-0">
            <p className="font-semibold truncate text-sm" data-testid="sidebar-user-name">{user?.name}</p>
            <p className="text-xs text-brand-orange font-bold uppercase tracking-wider">{ROLE_LABEL[user?.role]}</p>
          </div>
        </div>
        {hasPoints && (
          <div className="mt-4">
            <div className="flex justify-between text-xs mb-1.5">
              <span className="text-brand-muted">{stats.level_name || "Iniciante"}</span>
              <span className="font-bold text-white" data-testid="sidebar-points">{stats.points ?? 0} pts</span>
            </div>
            <div className="h-1.5 bg-brand-border rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-brand-blue to-brand-orange transition-all duration-700"
                style={{ width: `${stats.progress_pct ?? 0}%` }} />
            </div>
          </div>
        )}
      </div>

      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {items.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            onClick={() => setOpen(false)}
            data-testid={`nav-${item.label.toLowerCase()}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium transition-colors ${
                isActive
                  ? "bg-brand-blue/15 text-white border-l-2 border-brand-blue"
                  : "text-brand-muted hover:text-white hover:bg-white/5 border-l-2 border-transparent"
              }`
            }
          >
            <item.icon className="h-4.5 w-4.5" size={18} />
            {item.label}
          </NavLink>
        ))}
      </nav>

      <div className="p-3 border-t border-brand-border">
        <button
          onClick={handleLogout}
          data-testid="logout-button"
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium text-brand-muted hover:text-white hover:bg-destructive/15 transition-colors"
        >
          <LogOut size={18} /> Terminar sessão
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 shrink-0 flex-col fixed inset-y-0 border-r border-brand-border bg-[#0a0b0e]/80 backdrop-blur-xl">
        <SidebarInner />
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/70" onClick={() => setOpen(false)} />
          <aside className="relative w-64 bg-[#0a0b0e] border-r border-brand-border">
            <button className="absolute top-4 right-4 text-brand-muted" onClick={() => setOpen(false)}>
              <X size={20} />
            </button>
            <SidebarInner />
          </aside>
        </div>
      )}

      <div className="flex-1 lg:ml-64 flex flex-col min-w-0">
        <header style={accent ? { borderTop: `2px solid ${accent}` } : undefined}
          className="h-16 border-b border-brand-border bg-[#0a0b0e]/70 backdrop-blur-xl sticky top-0 z-30 flex items-center px-4 lg:px-8 gap-3">
          <button className="lg:hidden text-white" onClick={() => setOpen(true)} data-testid="mobile-menu-button">
            <Menu size={22} />
          </button>
          <div className="flex items-center gap-2 text-sm text-brand-muted">
            <span className="text-white font-semibold font-heading">A4G League</span>
            <ChevronRight size={14} />
            <span className="capitalize">{location.pathname.split("/")[2] || "Dashboard"}</span>
          </div>
          <div className="ml-auto flex items-center gap-4">
            {hasPoints && (
              <div style={accent ? { borderColor: accent } : undefined}
                className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-sm bg-brand-orange/10 border border-brand-orange/30">
                <Trophy size={15} style={accent ? { color: accent } : undefined} className="text-brand-orange" />
                <span className="text-sm font-bold text-white">{stats.points ?? 0}</span>
                <span className="text-xs text-brand-muted">pts</span>
              </div>
            )}
          </div>
        </header>

        <main className="flex-1 p-4 lg:p-8 max-w-[1400px] w-full mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
